import html2canvas from 'html2canvas';
import { OriginalStyle } from './types/style-types';
import { enhanceClonedDocument } from './document-enhancers';

/**
 * Progress callback type for canvas generation
 */
export type CanvasProgressCallback = (stage: string, progress?: number) => void;

/**
 * Generates a canvas from a DOM element with enhanced quality
 * Includes optional progress reporting
 */
export const generateCanvas = async (
  containerRef: HTMLDivElement,
  originalStyles: OriginalStyle[],
  progressCallback?: CanvasProgressCallback
): Promise<HTMLCanvasElement> => {
  try {
    // Validate container dimensions
    if (containerRef.scrollWidth <= 0 || containerRef.scrollHeight <= 0) {
      throw new Error("Container has invalid dimensions");
    }

    // Report progress if callback provided
    progressCallback?.("setup", 0);
    
    // Add a hidden canvas container to improve rendering
    const canvasContainer = document.createElement('div');
    canvasContainer.style.position = 'absolute';
    canvasContainer.style.left = '-9999px';
    // Add extra padding to the width to prevent clipping of the last stage card
    canvasContainer.style.width = `${containerRef.scrollWidth + 50}px`;
    canvasContainer.style.height = `${containerRef.scrollHeight}px`;
    document.body.appendChild(canvasContainer);
    
    // Clone the container to avoid modifying the original
    const clonedContainer = containerRef.cloneNode(true) as HTMLDivElement;
    // Apply extra padding to prevent right edge clipping
    clonedContainer.style.paddingRight = '50px';
    canvasContainer.appendChild(clonedContainer);
    
    progressCallback?.("rendering", 30);
    
    // Generate canvas with higher quality settings
    const canvas = await html2canvas(clonedContainer, {
      backgroundColor: '#ffffff',
      scale: 3, // Higher scale for sharper images
      useCORS: true,
      allowTaint: true,
      logging: false,
      imageTimeout: 15000, // 15 second timeout for image loading
      onclone: (document) => {
        progressCallback?.("enhancing", 45);
        enhanceClonedDocument(document);
      },
      removeContainer: false // Don't remove the container automatically
    });
    
    progressCallback?.("finalizing", 90);
    
    // Validate canvas dimensions
    if (canvas.width <= 0 || canvas.height <= 0) {
      throw new Error("Generated canvas has invalid dimensions");
    }
    
    // Clean up
    document.body.removeChild(canvasContainer);
    
    progressCallback?.("complete", 100);
    return canvas;
  } catch (error) {
    console.error("Error generating canvas:", error);
    throw error;
  }
};
